import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'

/**
 * The twelve analytics screens, and the strip that moves between them.
 *
 * ## Why twelve screens and not one dashboard
 *
 * PollSphere put all of this on one page. The result was a page where a
 * portfolio total, a per-ward coverage figure and a mobilizer's drop rate sat
 * within one screen height of each other with nothing to say that the first is
 * de-duplicated, the second is not, and the third is computed over a different
 * denominator. People read down a column and across a page; adjacency is a
 * claim of comparability whether or not anybody intended it.
 *
 * Each screen here answers one question, carries its own envelope badge, and
 * states its own exclusions. The filter object is shared — so moving between
 * screens keeps "youth programmes in Kisumu" — and nothing else is.
 *
 * ## The order is deliberate
 *
 * Portfolio → who we aimed at → who we hit twice → what it cost → who else is
 * in the room → where the holes are → the map → the money → did it work → was
 * it fair → is the data any good → tell me when it changes. A reader who walks
 * the strip left to right has been walked through the argument.
 */

const TABS: { to: string; label: string; hint: string }[] = [
  { to: '/programs/analytics', label: 'Overview', hint: 'Portfolio totals, de-duplicated' },
  { to: '/programs/analytics/council', label: 'AI Council', hint: 'The portfolio read as decisions, across every rollup' },
  { to: '/programs/analytics/target-groups', label: 'Target groups', hint: 'Who each programme aimed at' },
  { to: '/programs/analytics/overlap', label: 'Overlap', hint: 'People reached by more than one' },
  { to: '/programs/analytics/cost-vs-uplift', label: 'Cost vs result', hint: 'What one point of extra support cost to buy' },
  { to: '/programs/analytics/ecosystem', label: 'Ecosystem', hint: 'Partners, channels and units' },
  { to: '/programs/analytics/coverage', label: 'Coverage & gaps', hint: 'Where nobody was enrolled' },
  { to: '/programs/analytics/geography', label: 'Geography', hint: 'The same figures on a grid' },
  { to: '/programs/analytics/money', label: 'Money', hint: 'Spending, reconciliation, and whether the amounts look natural' },
  { to: '/programs/analytics/impact', label: 'Impact', hint: 'Measured change, and how many people it was measured on' },
  { to: '/programs/analytics/equity', label: 'Equity & reach', hint: 'Who is under-represented' },
  { to: '/programs/analytics/quality', label: 'Delivery quality', hint: 'Completeness and things to check' },
  { to: '/programs/analytics/alerts', label: 'Alerts', hint: 'What the nightly check flagged' },
]

export function ProgramAnalyticsLayout() {
  // The filter object lives in the query string, and moving between screens must
  // carry it. A bare `to` would drop it on every click, so "youth programmes in
  // Kisumu" would silently become "everything" the moment the reader moved from
  // coverage to money — and the two screens would then disagree for a reason
  // nothing on the page explains.
  const { search } = useLocation()

  return (
    <div className="space-y-4">
      <nav
        aria-label="Programme analytics"
        className="axis-table-scroll border-b border-border"
      >
        <ul className="flex items-stretch gap-1 min-w-max">
          {TABS.map((t) => (
            <li key={t.to}>
              <NavLink
                to={{ pathname: t.to, search }}
                // `end` only on the index tab — without it "Overview" stays lit
                // on every child route, which is the standard React Router trap
                // and reads as two tabs being selected at once.
                end={t.to === '/programs/analytics'}
                title={t.hint}
                className={({ isActive }) => cn(
                  'inline-block whitespace-nowrap px-3 py-2 text-erp-xs font-medium border-b-2 -mb-px transition-colors',
                  isActive
                    ? 'border-chocolate text-chocolate'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <Outlet />
    </div>
  )
}
